/**
 * Offerte-werkstroom: van "klant vraagt een prijs" tot "gewonnen of verloren".
 *
 * Waarom dit bestaat. Een offerte-aanvraag komt binnen als gewone order met status "offerte" en
 * álle regels op € 0 — de prijs is immers nog niet bekend. Tot 18-08-2026 kon je die prijs nergens
 * invullen: de admin had er geen veld voor en de server geen functie. De enige knop was
 * "Betaallink sturen", en die maakte dus een link voor alleen de verzendkosten. Offreren gebeurde
 * daardoor buiten het systeem om, waarna de klant niet kon betalen wat er was afgesproken.
 *
 * De fase leiden we AF uit de order in plaats van 'm apart bij te houden. Een los statusveld gaat
 * op termijn uit de pas lopen met de echte orderstatus (klant betaalt → order is verkocht, maar het
 * offerteveld zegt nog "verstuurd"). Afleiden kan niet uit de pas lopen.
 */
import { addVat } from "./pricing/vat";
import type { Cents } from "./types/money";
import type { Order, OrderItem, OrderStatus, OrderTotals } from "./types/order";

/** Fase in de offertetrechter, afgeleid uit orderstatus + of er al een prijs staat. */
export type QuoteStage = "nieuw" | "geprijsd" | "verstuurd" | "gewonnen" | "verloren";

export const QUOTE_STAGE_LABEL: Record<QuoteStage, string> = {
  nieuw: "Nieuw — prijs invullen",
  geprijsd: "Geprijsd — nog niet verstuurd",
  verstuurd: "Verstuurd — wacht op klant",
  gewonnen: "Gewonnen",
  verloren: "Verloren",
};

/** Statussen die betekenen: de klant heeft betaald, dus de offerte is gewonnen. */
const GEWONNEN: OrderStatus[] = [
  "paid", "te_beoordelen", "approved", "ordered_at_supplier",
  "confirmed_by_supplier", "shipped", "completed", "refunded",
];

/**
 * Is dit een offerte-order? We kijken naar de regels, niet naar een apart vlaggetje: bestaande
 * orders dragen dat vlaggetje niet en zouden bij een migratie buiten beeld vallen.
 */
export function isQuoteOrder(order: Pick<Order, "status" | "items">): boolean {
  if (order.status === "offerte") return true;
  return (order.items ?? []).some((i) => (i as OrderItem & { onRequest?: boolean }).onRequest === true);
}

/** Staat er al een prijs op de offerte? (Verzendkosten tellen niet mee — die staan er altijd.) */
export function isPriced(order: Pick<Order, "totals">): boolean {
  return (order.totals?.subtotal ?? 0) > 0;
}

export function quoteStage(order: Pick<Order, "status" | "items" | "totals">): QuoteStage {
  if (order.status === "cancelled") return "verloren";
  if (GEWONNEN.includes(order.status)) return "gewonnen";
  if (order.status === "awaiting_payment") return "verstuurd";
  return isPriced(order) ? "geprijsd" : "nieuw";
}

/** Hoeveel hele dagen ligt deze aanvraag er al? Voor de "langst open eerst"-sortering. */
export function daysOpen(order: Pick<Order, "createdAt">, now: number): number {
  const ms = now - (order.createdAt ?? now);
  return ms <= 0 ? 0 : Math.floor(ms / 86_400_000);
}

/**
 * Fases waarin de bal bij ÓNS ligt. Precies deze wil je bovenaan je werklijst: bij "verstuurd"
 * wacht je op de klant, bij gewonnen/verloren is het klaar.
 */
export function needsAction(stage: QuoteStage): boolean {
  return stage === "nieuw" || stage === "geprijsd";
}

/** Eén ingevulde regelprijs: welke regel, en wat kost hij per stuk (excl. btw). */
export interface QuoteLinePrice {
  index: number;
  unitPriceCents: Cents;
}

export interface QuotePricingResult {
  items: OrderItem[];
  totals: OrderTotals;
}

/**
 * Zet de ingevulde prijzen op de regels en rekent de order door.
 *
 * Btw wordt over subtotaal + verzending samen berekend, net als bij een gewone bestelling
 * (`addVat` op het excl.-bedrag) — anders wijkt de offerte een cent af van de factuur die er
 * later uit rolt, en dat kost je een telefoontje.
 *
 * Gooit bij onzin: een negatieve prijs, een regel die niet bestaat, of een bedrag boven een ton.
 * Dat laatste is een typefout-vangnet (een bedrag in centen invullen in plaats van euro's).
 */
export function priceQuote(
  items: OrderItem[],
  prices: QuoteLinePrice[],
  shippingExclCents: Cents,
  discountCents: Cents = 0,
): QuotePricingResult {
  const perIndex = new Map<number, Cents>();
  for (const p of prices) {
    if (!Number.isInteger(p.index) || p.index < 0 || p.index >= items.length) {
      throw new Error(`Regel ${p.index} bestaat niet op deze offerte.`);
    }
    if (!Number.isFinite(p.unitPriceCents) || p.unitPriceCents < 0) {
      throw new Error("Een regelprijs kan niet negatief zijn.");
    }
    if (p.unitPriceCents > 10_000_000) {
      throw new Error("Regelprijs boven € 100.000 — waarschijnlijk centen in plaats van euro's ingevuld.");
    }
    perIndex.set(p.index, Math.round(p.unitPriceCents));
  }

  const nieuw = items.map((item, i) => {
    const unit = perIndex.get(i);
    if (unit == null) return item;
    const qty = item.quantity ?? 1;
    return { ...item, unitPrice: unit, linePrice: unit * qty };
  });

  const subtotal = nieuw.reduce((t, i) => t + (i.linePrice ?? 0), 0);
  const shipping = Math.max(0, Math.round(shippingExclCents));
  const discount = Math.max(0, Math.round(discountCents));
  const excl = Math.max(0, subtotal - discount + shipping);
  const { vat, total } = addVat(excl);

  return {
    items: nieuw,
    totals: { subtotal, ...(discount > 0 ? { discount } : {}), shipping, vat, total },
  };
}
